/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import {
  Grid,
  Button,
  TextField,
  Link,
} from '@material-ui/core';
import {
  signUp,
  setIsUserRegistration,
  selectIsUserRegistration,
  selectIsPending,
} from '../../store/authSlice';
import {
  isRequired,
  noSpecialCharacters,
  validEmail,
  noPasswordMismatch,
  noSequentialNumbers,
  passwordValidationRules,
  validateField,
  validateForm,
} from '../../helpers/validation';
import PasswordField from '../common/PasswordField';

const useStyles = makeStyles((theme) => ({
  form: {
    width: '100%',
    marginTop: theme.spacing(1),
  },
  submit: {
    margin: theme.spacing(3, 0, 2),
  },
}));

export default function SignUpForm() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const isPending = useSelector(selectIsPending);
  const isUserRegistration = useSelector(selectIsUserRegistration);
  const [formErrors, setFormErrors] = useState({});
  const [formValues, setFormValues] = useState({
    firstName: '',
    lastName: '',
    username: '',
    password: '',
    email: '',
  });
  const [confirmPassword, setConfirmPassword] = useState('');
  const [mismatchError, setMismatchError] = useState('');

  const formRules = {
    firstName: [isRequired, noSpecialCharacters, noSequentialNumbers(8)],
    lastName: [isRequired, noSpecialCharacters, noSequentialNumbers(8)],
    username: [isRequired, noSpecialCharacters, noSequentialNumbers(8)],
    email: [isRequired, validEmail],
    password: [...passwordValidationRules],
  };

  const handleChange = (event) => {
    setFormValues({ ...formValues, [event.target.name]: event.target.value });
  };

  const handleBlur = (event) => {
    validateField(formRules, formErrors, setFormErrors, event.target.name, event.target.value);
  };

  const handleConfirmPasswordChange = (event) => {
    setConfirmPassword(event.target.value);
  };

  const handleSignInClick = (event) => {
    event.preventDefault();
    dispatch(setIsUserRegistration(false));
  };

  const validateNewPassword = () => {
    if (!isRequired.validate(confirmPassword)) {
      setMismatchError(isRequired.error);
      return false;
    }

    if (!noPasswordMismatch(formValues.password).validate(confirmPassword)) {
      setMismatchError(noPasswordMismatch(formValues.password).error);
      return false;
    }

    setMismatchError('');
    return true;
  };

  const attemptSignUp = (event) => {
    event.preventDefault();
    if (validateForm(formRules, formValues, formErrors, setFormErrors) && validateNewPassword()) {
      dispatch(signUp(formValues));
    }
  };

  // Reset values when form is displayed
  useEffect(() => {
    if (isUserRegistration) {
      setMismatchError('');
      setConfirmPassword('');
      setFormValues({
        firstName: '',
        lastName: '',
        username: '',
        password: '',
        email: '',
      });
    }
  }, [isUserRegistration]);

  return (
    <form className={classes.form}>
      <Grid container spacing={3}>
        <Grid align="left" item md={6}>
          <TextField
            variant="outlined"
            margin="normal"
            id="signup-firstanme"
            label="First Name"
            name="firstName"
            autoComplete="given-name"
            value={formValues.firstName}
            error={!!formErrors.firstName}
            helperText={formErrors.firstName}
            onChange={handleChange}
            onBlur={handleBlur}
            disabled={isPending}
            fullWidth
            required
          />
        </Grid>
        <Grid align="right" item md={6}>
          <TextField
            variant="outlined"
            margin="normal"
            id="signup-lastname"
            label="Last Name"
            name="lastName"
            autoComplete="family-name"
            value={formValues.lastName}
            error={!!formErrors.lastName}
            helperText={formErrors.lastName}
            onChange={handleChange}
            onBlur={handleBlur}
            disabled={isPending}
            fullWidth
            required
          />
        </Grid>
      </Grid>
      <TextField
        variant="outlined"
        margin="normal"
        id="signup-username"
        label="Username"
        name="username"
        autoComplete="username"
        value={formValues.username}
        error={!!formErrors.username}
        helperText={formErrors.username}
        onChange={handleChange}
        onBlur={handleBlur}
        disabled={isPending}
        fullWidth
        required
      />
      <TextField
        variant="outlined"
        margin="normal"
        id="signup-email"
        label="Email"
        name="email"
        autoComplete="email"
        value={formValues.email}
        onChange={handleChange}
        onBlur={handleBlur}
        error={!!formErrors.email}
        helperText={formErrors.email}
        disabled={isPending}
        fullWidth
        required
      />
      <PasswordField
        variant="outlined"
        margin="normal"
        name="password"
        label="New Password"
        id="signup-password"
        value={formValues.password}
        onChange={handleChange}
        onBlur={handleBlur}
        error={!!formErrors.password}
        helperText={formErrors.password}
        disabled={isPending}
        fullWidth
        required
      />
      <PasswordField
        variant="outlined"
        margin="normal"
        name="confirm-password"
        label="Confirm Password"
        id="signup-confirm-password"
        value={confirmPassword}
        onChange={handleConfirmPasswordChange}
        onBlur={validateNewPassword}
        error={!!mismatchError}
        helperText={mismatchError}
        disabled={isPending}
        fullWidth
        required
      />
      <Button
        type="submit"
        variant="contained"
        color="primary"
        className={classes.submit}
        onClick={attemptSignUp}
        disabled={isPending}
        fullWidth
      >
        Sign Up
      </Button>
      <Grid container>
        <Grid item xs>
          <Link component="button" onClick={handleSignInClick} variant="body2">
            Already have an account? Sign in
          </Link>
        </Grid>
      </Grid>
    </form>
  );
}
